import type { SharingDecision } from '@ralph/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  decideSharingRequest,
  fetchMySharingRequests,
  fetchSharingQueue,
  fetchSharingRequest,
  withdrawSharingRequest
} from '@/api/sharing.ts'

/**
 * Sharing requests (PRD-26 D-29), as queries.
 *
 * A decision or a withdrawal changes who can use something, so it invalidates the item's every
 * reading — the maker's agent, the knowledge base, the gallery, coverage — as well as the queue.
 * The queue refetches on focus: an administrator returning to it after lunch must not decide on a
 * card whose digest moved an hour ago (the server would refuse it, but the reader should not have
 * to find out that way).
 */

export const sharingKeys = {
  all: ['sharing'] as const,
  mine: ['sharing', 'mine'] as const,
  queue: (status: 'pending' | 'all') => ['sharing', 'queue', status] as const,
  request: (id: string) => ['sharing', 'request', id] as const
}

export const useMySharingRequests = (enabled = true) =>
  useQuery({ queryKey: sharingKeys.mine, queryFn: fetchMySharingRequests, enabled, retry: false })

export const useSharingRequest = (id: string | null) =>
  useQuery({
    queryKey: sharingKeys.request(id ?? ''),
    queryFn: () => fetchSharingRequest(id ?? ''),
    enabled: Boolean(id),
    retry: false,
    refetchOnWindowFocus: true
  })

/** Administrators only; the shell's Governance count reads the pending length. */
export const useSharingQueue = (status: 'pending' | 'all', enabled: boolean) =>
  useQuery({
    queryKey: sharingKeys.queue(status),
    queryFn: () => fetchSharingQueue(status),
    enabled,
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 15_000
  })

const useSharingInvalidation = () => {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: sharingKeys.all })
    void client.invalidateQueries({ queryKey: ['maker'] })
    void client.invalidateQueries({ queryKey: ['marketplace'] })
    void client.invalidateQueries({ queryKey: ['knowledge-bases'] })
    void client.invalidateQueries({ queryKey: ['notifications'] })
    // An approval changes what is shared with a group, and every live reach it touches.
    void client.invalidateQueries({ queryKey: ['groups', 'detail'] })
    void client.invalidateQueries({ queryKey: ['groups', 'reach'] })
  }
}

export const useDecideSharingRequest = () => {
  const invalidate = useSharingInvalidation()
  return useMutation({
    mutationFn: ({ id, ...decision }: SharingDecision & { id: string }) => decideSharingRequest(id, decision),
    // A refusal (a stale digest, a request decided a moment ago) also means the card is out of
    // date, so the queue re-reads either way.
    onSettled: invalidate
  })
}

export const useWithdrawSharingRequest = () => {
  const invalidate = useSharingInvalidation()
  return useMutation({ mutationFn: (id: string) => withdrawSharingRequest(id), onSettled: invalidate })
}
